import React, { useState, useEffect, useRef, useCallback } from 'react';
import AbacusSVG from './AbacusSVG';

const AbacusGame = ({ gameSession, onGameComplete, onBack }) => {
  const [gameState, setGameState] = useState('ready'); // 'ready', 'playing', 'finished'
  const [abacusStyle, setAbacusStyle] = useState('soroban'); // 'soroban' | 'standard'
  const [abacusValue, setAbacusValue] = useState([]);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [challengeIndex, setChallengeIndex] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [feedbackAnim, setFeedbackAnim] = useState(''); // 'celebrate' | 'shake' | ''
  const [gameData, setGameData] = useState({
    challenges: [],
    answers: [],
    startTime: null,
    endTime: null,
  });

  const timerRef = useRef(null);
  const totalChallenges = 10;

  // Australian Curriculum-aligned grade mappings
  const GRADE_ABACUS_MAPPINGS = {
    'Year 3':  { maxNumber: 20,    numRods: 2, operations: ['represent'],           description: 'Represent numbers to 20 on the abacus' },
    'Year 4':  { maxNumber: 100,   numRods: 3, operations: ['represent'],           description: 'Represent numbers to 100 on the abacus' },
    'Year 5':  { maxNumber: 999,   numRods: 3, operations: ['represent', '+'],      description: 'Represent and add numbers to 999' },
    'Year 6':  { maxNumber: 9999,  numRods: 4, operations: ['represent', '+'],      description: 'Represent and add numbers to 9,999' },
    'Year 7':  { maxNumber: 9999,  numRods: 4, operations: ['represent', '+', '-', '×'], description: 'All operations on the abacus' },
    'Year 8':  { maxNumber: 9999,  numRods: 4, operations: ['represent', '+', '-', '×'], description: 'Speed and fluency with abacus' },
    'Year 9':  { maxNumber: 9999,  numRods: 4, operations: ['represent', '+', '-', '×'], description: 'Advanced abacus mental math' },
    'Year 10': { maxNumber: 9999,  numRods: 4, operations: ['represent', '+', '-', '×'], description: 'Expert abacus calculations' },
    'Year 11': { maxNumber: 9999,  numRods: 4, operations: ['represent', '+', '-', '×'], description: 'Abacus speed challenges' },
    'Year 12': { maxNumber: 9999,  numRods: 4, operations: ['represent', '+', '-', '×'], description: 'University-level abacus mastery' },
  };

  const getDifficultySettings = () => {
    const difficulty = gameSession?.difficulty || 'easy';
    const gradeLevel = gameSession?.grade_level;
    const settings = gameSession?.game?.difficulty_levels?.[difficulty];

    let base;
    switch (difficulty) {
      case 'easy':
        base = {
          numRods: settings?.numRods || 2,
          maxNumber: settings?.maxNumber || 20,
          operations: settings?.operations || ['represent'],
          timeLimit: settings?.timeLimit || null,
        };
        break;
      case 'medium':
        base = {
          numRods: settings?.numRods || 3,
          maxNumber: settings?.maxNumber || 100,
          operations: settings?.operations || ['represent', '+'],
          timeLimit: settings?.timeLimit || 180,
        };
        break;
      case 'hard':
        base = {
          numRods: settings?.numRods || 4,
          maxNumber: settings?.maxNumber || 9999,
          operations: settings?.operations || ['represent', '+', '-', '×'],
          timeLimit: settings?.timeLimit || 180,
        };
        break;
      default:
        base = { numRods: 2, maxNumber: 20, operations: ['represent'], timeLimit: null };
    }

    // Grade enhancement
    if (gradeLevel && GRADE_ABACUS_MAPPINGS[gradeLevel]) {
      const curriculum = GRADE_ABACUS_MAPPINGS[gradeLevel];
      return {
        ...base,
        maxNumber: Math.min(base.maxNumber, curriculum.maxNumber),
        numRods: Math.min(base.numRods, curriculum.numRods),
        operations: base.operations.filter(op => curriculum.operations.includes(op)),
        gradeInfo: curriculum.description,
      };
    }

    return base;
  };

  const randomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const generateChallenge = () => {
    const settings = getDifficultySettings();
    const ops = settings.operations;
    const operation = ops[Math.floor(Math.random() * ops.length)];

    switch (operation) {
      case '+': {
        const maxA = Math.floor(settings.maxNumber * 0.6);
        const a = randomNumber(1, maxA);
        const b = randomNumber(1, Math.min(settings.maxNumber - a, maxA));
        return {
          type: 'addition',
          operationLabel: 'Addition',
          displayText: `${a} + ${b} = ?`,
          targetNumber: a + b,
          correctAnswer: a + b,
          timestamp: Date.now(),
        };
      }
      case '-': {
        const minuend = randomNumber(2, settings.maxNumber);
        const subtrahend = randomNumber(1, minuend - 1);
        return {
          type: 'subtraction',
          operationLabel: 'Subtraction',
          displayText: `${minuend} − ${subtrahend} = ?`,
          targetNumber: minuend - subtrahend,
          correctAnswer: minuend - subtrahend,
          timestamp: Date.now(),
        };
      }
      case '×': {
        // Keep factors small so product fits within maxNumber
        const maxFactor = Math.min(12, Math.floor(Math.sqrt(settings.maxNumber)));
        const a = randomNumber(2, maxFactor);
        const b = randomNumber(2, Math.min(maxFactor, Math.floor(settings.maxNumber / a)));
        return {
          type: 'multiplication',
          operationLabel: 'Multiplication',
          displayText: `${a} × ${b} = ?`,
          targetNumber: a * b,
          correctAnswer: a * b,
          timestamp: Date.now(),
        };
      }
      default: {
        // represent
        const target = randomNumber(1, settings.maxNumber);
        return {
          type: 'represent',
          operationLabel: 'Represent',
          displayText: `Represent the number:`,
          targetNumber: target,
          correctAnswer: target,
          timestamp: Date.now(),
        };
      }
    }
  };

  // Convert abacus value array → single number
  const getAbacusNumber = (valArray) => {
    return valArray.reduce((sum, digit, index) => sum + digit * Math.pow(10, index), 0);
  };

  // Reset abacus to all zeros
  const resetAbacus = () => {
    const settings = getDifficultySettings();
    setAbacusValue(Array(settings.numRods).fill(0));
  };

  // Start the game
  const startGame = () => {
    const settings = getDifficultySettings();
    setGameState('playing');
    setChallengeIndex(0);
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setStreak(0);
    setBestStreak(0);
    setAbacusValue(Array(settings.numRods).fill(0));
    setGameData({
      challenges: [],
      answers: [],
      startTime: Date.now(),
      endTime: null,
    });

    const first = generateChallenge();
    setCurrentChallenge(first);

    // Start timer if applicable
    if (settings.timeLimit) {
      setTimeLeft(settings.timeLimit);
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            endGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Submit answer
  const submitAnswer = useCallback(() => {
    if (!currentChallenge || showFeedback || gameState !== 'playing') return;

    const userNum = getAbacusNumber(abacusValue);
    const correct = userNum === currentChallenge.correctAnswer;

    setGameData((prev) => ({
      ...prev,
      challenges: [...prev.challenges, currentChallenge],
      answers: [
        ...prev.answers,
        {
          userAnswer: userNum,
          correctAnswer: currentChallenge.correctAnswer,
          isCorrect: correct,
          timeSpent: Date.now() - currentChallenge.timestamp,
          abacusState: [...abacusValue],
        },
      ],
    }));

    setQuestionsAnswered((prev) => prev + 1);
    if (correct) {
      setCorrectAnswers((prev) => prev + 1);
      setStreak((prev) => {
        const newStreak = prev + 1;
        setBestStreak((best) => Math.max(best, newStreak));
        return newStreak;
      });
      setFeedbackAnim('celebrate');
    } else {
      setStreak(0);
      setFeedbackAnim('shake');
    }

    setIsCorrect(correct);
    setShowFeedback(true);

    setTimeout(() => {
      setShowFeedback(false);
      setFeedbackAnim('');
      const nextIndex = challengeIndex + 1;

      if (nextIndex >= totalChallenges) {
        endGame();
      } else {
        setChallengeIndex(nextIndex);
        resetAbacus();
        setCurrentChallenge(generateChallenge());
      }
    }, 1200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChallenge, showFeedback, gameState, abacusValue, challengeIndex]);

  // End game
  const endGame = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setGameState('finished');

    setGameData((currentGameData) => {
      const finalData = { ...currentGameData, endTime: Date.now() };
      const totalTime = Math.floor((finalData.endTime - finalData.startTime) / 1000);
      const totalQ = finalData.answers.length;
      const correctCount = finalData.answers.filter((a) => a.isCorrect).length;
      const score = totalQ > 0 ? Math.round((correctCount / totalQ) * 100) : 0;

      const basePoints = correctCount * 5;
      const speedBonus = timeLeft && timeLeft > 0 ? Math.floor(timeLeft / 9) : 0;

      onGameComplete({
        score,
        timeTaken: totalTime,
        questionsAttempted: totalQ,
        correctAnswers: correctCount,
        gameData: { ...finalData, abacusStyle, bestStreak },
        pointsEarned: basePoints + speedBonus,
      });
      return finalData;
    });
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    if (gameState !== 'playing') return;
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !showFeedback) {
        e.preventDefault();
        submitAnswer();
      } else if ((e.key === 'r' || e.key === 'R') && !showFeedback) {
        e.preventDefault();
        resetAbacus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, showFeedback, submitAnswer]);

  // ─── READY STATE ───
  if (gameState === 'ready') {
    const settings = getDifficultySettings();
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-400 to-cyan-500 p-8 text-white text-center">
            <div className="text-5xl mb-4">🧮</div>
            <h1 className="text-3xl font-bold mb-2">Digital Abacus</h1>
            <p className="text-teal-100">
              Difficulty: <span className="font-semibold capitalize">{gameSession?.difficulty}</span>
            </p>
          </div>

          {/* Instructions */}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Game Instructions</h2>

            <div className="space-y-4 mb-8">
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-4 text-teal-600 font-bold">1</div>
                <div>You'll be given <strong>{totalChallenges} challenges</strong> to solve on the abacus</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-4 text-teal-600 font-bold">2</div>
                <div>
                  {abacusStyle === 'soroban'
                    ? <>Move beads: <strong>top bead = 5</strong>, each <strong>bottom bead = 1</strong></>
                    : <>Click beads to <strong>count up</strong> on each rod</>
                  }
                </div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-4 text-teal-600 font-bold">3</div>
                <div>Click <strong>Submit</strong> when the abacus shows your answer</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mr-4 text-teal-600 font-bold">4</div>
                <div>Earn <strong>5 points per correct answer</strong> + speed bonus</div>
              </div>
            </div>

            {/* Abacus Style Selector */}
            <div className="bg-gray-50 rounded-lg p-5 mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Choose Abacus Style:</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setAbacusStyle('soroban')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    abacusStyle === 'soroban'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">🇯🇵</div>
                  <div className="font-semibold text-gray-900">Soroban</div>
                  <div className="text-xs text-gray-500">Japanese style — 1 top bead (5) + 4 bottom beads (1)</div>
                </button>
                <button
                  onClick={() => setAbacusStyle('standard')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    abacusStyle === 'standard'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl mb-1">🔢</div>
                  <div className="font-semibold text-gray-900">Standard</div>
                  <div className="text-xs text-gray-500">Counting style — 10 beads per rod, click to count</div>
                </button>
              </div>
            </div>

            {/* Difficulty Info */}
            <div className="bg-teal-50 rounded-lg p-4 mb-8">
              <h3 className="font-semibold text-teal-900 mb-2">
                {gameSession?.difficulty?.charAt(0).toUpperCase() + gameSession?.difficulty?.slice(1)} Mode
                {gameSession?.grade_level && (
                  <span className="text-sm font-normal text-teal-700 ml-2">
                    • {gameSession.grade_level}
                  </span>
                )}
              </h3>
              <p className="text-teal-800 text-sm">
                {settings.numRods} rods, numbers 1–{settings.maxNumber.toLocaleString()},{' '}
                {settings.operations.join(', ')} operations
                {settings.timeLimit && ` • ${Math.floor(settings.timeLimit / 60)} min timer`}
              </p>
              {settings.gradeInfo && (
                <p className="text-teal-700 text-xs mt-1">Australian Curriculum: {settings.gradeInfo}</p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-4">
              <button
                onClick={onBack}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-medium transition-colors"
              >
                Back to Games
              </button>
              <button
                onClick={startGame}
                className="flex-[2] bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white py-3 px-8 rounded-lg font-medium transition-all duration-200 transform hover:scale-105"
              >
                Start Game! 🚀
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── PLAYING STATE ───
  if (gameState === 'playing') {
    const settings = getDifficultySettings();
    const currentValue = getAbacusNumber(abacusValue);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Game Header */}
          <div className="bg-gradient-to-r from-teal-400 to-cyan-500 p-5 text-white">
            <div className="flex justify-between items-center">
              <div className="text-center">
                <div className="text-xl font-bold">{challengeIndex + 1}/{totalChallenges}</div>
                <div className="text-xs text-teal-100">Question</div>
              </div>

              {timeLeft !== null && (
                <div className="text-center">
                  <div className={`text-xl font-bold ${timeLeft <= 30 ? 'text-yellow-200 animate-pulse' : ''}`}>
                    {formatTime(timeLeft)}
                  </div>
                  <div className="text-xs text-teal-100">Time Left</div>
                </div>
              )}

              <div className="text-3xl">🧮</div>

              <div className="text-center">
                <div className="text-xl font-bold">{correctAnswers}/{questionsAnswered}</div>
                <div className="text-xs text-teal-100">Correct</div>
              </div>
            </div>
          </div>

          {/* Challenge area */}
          <div className="p-6">
            {showFeedback ? (
              <div className={`text-center py-8 ${feedbackAnim === 'celebrate' ? 'animate-bounce' : ''} ${feedbackAnim === 'shake' ? 'animate-pulse' : ''}`}>
                <div className={`text-6xl mb-4 ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                  {isCorrect ? '✅' : '❌'}
                </div>
                <div className={`text-2xl font-bold ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                  {isCorrect ? (streak >= 3 ? `${streak} in a row! 🔥` : 'Correct!') : 'Not quite!'}
                </div>
                {!isCorrect && currentChallenge && (
                  <div className="text-lg text-gray-600 mt-2">
                    The answer was <strong>{currentChallenge.correctAnswer}</strong>
                  </div>
                )}
                {isCorrect && streak >= 5 && (
                  <div className="text-sm text-orange-500 font-semibold mt-1">Streak Bonus!</div>
                )}
              </div>
            ) : (
              <>
                {/* Challenge prompt */}
                <div className="text-center mb-6">
                  {/* Operation type badge */}
                  <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 ${
                    currentChallenge?.type === 'represent' ? 'bg-blue-100 text-blue-700' :
                    currentChallenge?.type === 'addition' ? 'bg-green-100 text-green-700' :
                    currentChallenge?.type === 'subtraction' ? 'bg-orange-100 text-orange-700' :
                    currentChallenge?.type === 'multiplication' ? 'bg-purple-100 text-purple-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {currentChallenge?.operationLabel || 'Challenge'}
                  </span>

                  {currentChallenge?.type === 'represent' ? (
                    <>
                      <p className="text-gray-500 text-sm mb-1">{currentChallenge?.displayText}</p>
                      <div className="text-5xl font-bold text-gray-800 font-mono">
                        {currentChallenge?.targetNumber}
                      </div>
                    </>
                  ) : (
                    <div className="text-3xl font-bold text-gray-800 font-mono mt-1">
                      {currentChallenge?.displayText}
                    </div>
                  )}
                </div>

                {/* Streak indicator */}
                {streak >= 2 && (
                  <div className="text-center mb-3">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                      🔥 {streak} streak
                    </span>
                  </div>
                )}

                {/* Abacus */}
                <div className="mb-4">
                  <AbacusSVG
                    value={abacusValue}
                    onChange={setAbacusValue}
                    numRods={settings.numRods}
                    interactive={true}
                    showLabels={true}
                    style={abacusStyle}
                  />
                </div>

                {/* Current value display */}
                <div className="text-center mb-6">
                  <div className={`inline-block rounded-xl px-6 py-3 transition-colors duration-200 ${
                    currentValue === currentChallenge?.correctAnswer
                      ? 'bg-green-50 ring-2 ring-green-300'
                      : 'bg-gray-100'
                  }`}>
                    <span className="text-sm text-gray-500">Your answer: </span>
                    <span className={`text-2xl font-bold font-mono ${
                      currentValue === currentChallenge?.correctAnswer ? 'text-green-600' : 'text-gray-800'
                    }`}>{currentValue}</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={resetAbacus}
                    className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors text-sm"
                    title="Reset abacus (R)"
                  >
                    Reset <span className="hidden sm:inline text-xs text-gray-400 ml-1">(R)</span>
                  </button>
                  <button
                    onClick={submitAnswer}
                    className="px-8 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white rounded-lg font-medium transition-all transform hover:scale-105 text-sm"
                    title="Submit answer (Enter)"
                  >
                    Submit <span className="hidden sm:inline text-xs text-teal-200 ml-1">(Enter)</span>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Progress bar */}
          <div className="px-6 pb-5">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-teal-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((challengeIndex) / totalChallenges) * 100}%` }}
              />
            </div>
            <div className="text-center text-xs text-gray-500 mt-1">
              Progress: {challengeIndex}/{totalChallenges}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null; // 'finished' — parent handles via GameResults
};

export default AbacusGame;
